/**
 * WVB-02 — Budget Versions Tab (ADR-B11)
 * Read-only list of all budget versions for an event.
 * Shows: version, status, creator, timestamps, currency, total, lines.
 * Approved/submitted snapshots are not editable through this UI.
 */
import { useState, useEffect } from "react";
import { Loader2, Clock, ChevronRight, X } from "lucide-react";
import { budgetApi } from "@/services/api";
import { BUDGET_STATUS_CONFIG } from "./EventFinancialsUtils";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const cfg = BUDGET_STATUS_CONFIG[status] || { cls: "bg-slate-100 text-slate-500", label: status };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function VersionDetailModal({ eid, version, onClose }) {
  const [lines, setLines] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [bRes, lRes] = await Promise.all([
          budgetApi.getBudgetById(eid, version.id),
          budgetApi.getLines(eid, version.id),
        ]);
        setDetail(bRes.data);
        setLines(lRes.data?.lines || []);
      } catch { /* ignore — version may have no lines */ }
      finally { setLoading(false); }
    }
    load();
  }, [eid, version.id]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="version-detail-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-900 text-lg">
              {version.budget_number} · v{version.budget_version}
            </span>
            <StatusBadge status={version.status} />
            {(version.status === "APPROVED" || version.status === "SUBMITTED") && (
              <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                Read-only snapshot
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            data-testid="version-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
          </div>
        ) : (
          <div className="p-6 space-y-5">

            {/* Version metadata grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400 mb-0.5">Total Forecast</p>
                <p className="font-bold text-indigo-700 text-base">{version.total_forecast_display}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400 mb-0.5">Currency</p>
                <p className="font-semibold text-slate-700">{version.currency_code}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400 mb-0.5">Created</p>
                <p className="text-slate-600 text-xs">{fmt(version.created_at)}</p>
                <p className="text-slate-400 text-xs truncate">{version.created_by_user_id}</p>
              </div>
              {version.submitted_at ? (
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="text-xs text-blue-400 mb-0.5">Submitted</p>
                  <p className="text-blue-700 text-xs">{fmt(version.submitted_at)}</p>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-400 mb-0.5">Submitted</p>
                  <p className="text-slate-400 text-xs">—</p>
                </div>
              )}
            </div>

            {/* Budget totals breakdown from detail */}
            {detail && (
              <div className="flex flex-wrap gap-4 bg-slate-50 rounded-xl p-4 text-xs">
                <div>
                  <span className="text-slate-400">Direct Cost: </span>
                  <span className="font-semibold text-slate-700">{detail.total_direct_cost_display || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Contingency: </span>
                  <span className="font-semibold text-amber-600">{detail.total_contingency_display || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Tax: </span>
                  <span className="font-semibold text-slate-600">{detail.total_tax_display || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Forecast Total: </span>
                  <span className="font-bold text-indigo-700">{detail.total_forecast_display || "—"}</span>
                </div>
              </div>
            )}

            {/* Notes */}
            {version.notes && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm text-amber-800">
                <span className="font-semibold">Notes: </span>{version.notes}
              </div>
            )}

            {/* Previous version reference */}
            {version.previous_version_id && (
              <div className="text-xs text-slate-400">
                Supersedes version ID: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{version.previous_version_id}</code>
              </div>
            )}

            {/* Historical lines table */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">
                Budget Lines ({lines.length})
              </p>
              {lines.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center bg-slate-50 rounded-xl">
                  No lines in this version.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm" data-testid="version-detail-lines-table">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="px-3 py-2.5 text-left font-semibold">Description</th>
                        <th className="px-3 py-2.5 text-left font-semibold">Category</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Qty</th>
                        <th className="px-3 py-2.5 text-left font-semibold">UoM</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Rate</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Cont %</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Tax %</th>
                        <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lines.map(l => (
                        <tr key={l.id} className="hover:bg-slate-50">
                          <td className="px-3 py-2.5 text-slate-800">{l.description}</td>
                          <td className="px-3 py-2.5">
                            <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              {l.budget_category}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-600">{l.quantity}</td>
                          <td className="px-3 py-2.5 text-slate-400 text-xs">{l.unit_of_measure}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700 font-medium">{l.unit_rate_display}</td>
                          <td className="px-3 py-2.5 text-right text-slate-500">{l.contingency_percent}%</td>
                          <td className="px-3 py-2.5 text-right text-slate-500">{l.tax_percent}%</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-slate-800">{l.total_line_display}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EventBudgetVersionsTab({ eid, currentBudgetId }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    budgetApi.getVersions(eid)
      .then(r => setVersions(r.data?.versions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 gap-3 text-slate-400" data-testid="no-versions">
        <Clock className="w-10 h-10 text-slate-300" />
        <p className="text-sm">No budget versions yet.</p>
        <p className="text-xs text-slate-300">Create a budget in the Overview tab to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="budget-versions-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">
          {versions.length} version{versions.length !== 1 ? "s" : ""} — Approved and submitted snapshots are read-only.
        </p>
      </div>

      {versions.map(v => (
        <div
          key={v.id}
          className={`bg-white rounded-xl border p-4 flex items-center justify-between flex-wrap gap-3
            cursor-pointer hover:bg-slate-50 transition-colors
            ${v.id === currentBudgetId ? "border-indigo-300 ring-1 ring-indigo-100" : "border-slate-200"}`}
          onClick={() => setSelected(v)}
          data-testid={`version-row-${v.id}`}
        >
          <div className="flex items-center gap-3 flex-wrap">
            {v.id === currentBudgetId && (
              <span className="text-xs bg-indigo-50 text-indigo-600 font-semibold px-2 py-0.5 rounded-full border border-indigo-100">
                Active
              </span>
            )}
            <span className="font-bold text-slate-900">{v.budget_number}</span>
            <span className="text-slate-400 text-sm">v{v.budget_version}</span>
            <StatusBadge status={v.status} />
          </div>

          <div className="flex items-center gap-5 text-sm">
            <div>
              <span className="text-slate-400 text-xs">Total </span>
              <span className="font-semibold text-indigo-700">{v.total_forecast_display}</span>
            </div>
            <div>
              <span className="text-slate-400 text-xs">Created </span>
              <span className="text-slate-600 text-xs">{fmt(v.created_at)}</span>
            </div>
            {v.submitted_at && (
              <div>
                <span className="text-slate-400 text-xs">Submitted </span>
                <span className="text-slate-600 text-xs">{fmt(v.submitted_at)}</span>
              </div>
            )}
            <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
          </div>
        </div>
      ))}

      {selected && (
        <VersionDetailModal eid={eid} version={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
